import {
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  model,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import {
  FLOW_GATEWAY,
  getWorkObjectIconByWorkObjectTypeId,
  IWHWorkObjectEndpointConfigModel,
  WHDataService,
  WHFeatureKeyENUM,
  WHIconENUM,
  WHRouteZeroENUM,
  WHWorkObjectDataService,
  WHWorkObjectDOM,
  IWHWorkObjectDTO,
  WHMetadataDataService,
  WHLoginDataService,
  IWHUIConfigModel,
  workObjectUiConfig$,
  IWHLocationDTO,
  IWHFlowContactDTO,
} from '@workheld/workheld-shared-lib';
import { DataModelHelperService } from 'src/app/app-services-helper/data-model-helper.service';
import {
  catchError,
  EMPTY,
  filter,
  first,
  firstValueFrom,
  switchMap,
} from 'rxjs';
import { ErrorHandlerService } from 'src/app/app-services-async/w-h-error-handler.service';
import { AppDialogService } from 'src/app/app-dialogs/app-dialog.service';
import { environment } from 'src/environments/environment';
import { Store } from '@ngrx/store';
import { IAppStore } from 'src/app/app-stores/index.stores';
import { SetWHWorkObjectDOM } from 'src/app/app-stores/w-h-work-object-stores/w-h-work-object-crud-store/w-h-work-object-crud-store.actions';
import { Router } from '@angular/router';

@Component({
  selector: 'ng-bee-work-object-static-data',
  templateUrl: './work-object-static-data.component.html',
  styleUrls: ['./work-object-static-data.component.scss'],
  standalone: false,
})
export class WorkObjectStaticDataComponent {
  // SERVICES AND DEPENDENCIES INJECTION
  private readonly loginDataService = inject(WHLoginDataService);
  private readonly dataService = inject(WHDataService);
  private readonly store = inject(Store<IAppStore>);
  private readonly dataModelHelperService = inject(DataModelHelperService);
  private readonly metadataDataService = inject(WHMetadataDataService);
  private readonly workObjectDataService = inject(WHWorkObjectDataService);
  private readonly appDialogService = inject(AppDialogService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  // INPUTS AND MODELS
  public readonly isEndTimeVisible = input(false);
  public readonly isSimpleData = input(false);
  public readonly withTitleLabel = input(false);
  public readonly titleLabel = model('workobject.model.root');
  public readonly isExternalInput = input(false);
  public readonly isWorkObjectDetail = input(false);
  public readonly detailURL = input<string>();
  public readonly workObjectDOM = model<WHWorkObjectDOM>();
  public readonly sumOfPlannedWorkTime = input(0);
  public readonly location = input<IWHLocationDTO>();
  public readonly isWorkObjectActive = input<boolean>();

  // FEATURE CONFIG
  private readonly featureConfigMap = this.loginDataService.featureConfigMap;
  public readonly isLocationEnabled = computed(() =>
    this.featureConfigMap().get(
      WHFeatureKeyENUM.CORE_WORK_FLOW_WORK_ORDER_LOCATION,
    ),
  );
  public readonly isWorkObjectLocationEnabled = computed(() =>
    this.featureConfigMap().get(
      WHFeatureKeyENUM.CORE_WORK_FLOW_WORK_OBJECT_LOCATION,
    ),
  );
  public readonly isTargetStateEnabled = computed(() =>
    this.featureConfigMap().get(
      WHFeatureKeyENUM.CORE_WORK_FLOW_WORK_ORDER_TARGET_STATE,
    ),
  );
  public readonly isPlannedWorkEnabled = computed(() =>
    this.featureConfigMap().get(WHFeatureKeyENUM.PLANNED_WORK),
  );

  // WH ENUMS
  private readonly metadataTranslation = toSignal(
    this.metadataDataService.metadataTranslation$,
  );
  public readonly PRIORITY = computed(
    () => this.metadataTranslation()?.PRIORITY,
  );
  public readonly SLA = computed(() => this.metadataTranslation()?.SLA);
  public readonly CATEGORY = computed(
    () => this.metadataTranslation()?.CATEGORY,
  );
  public readonly WORK_OBJECT_CATEGORY = computed(
    () => this.metadataTranslation()?.WORK_OBJECT_CATEGORY,
  );
  public readonly WORK_OBJECT_TARGET_STATE = computed(
    () => this.metadataTranslation()?.WORK_OBJECT_TARGET_STATE,
  );
  public readonly CONTACT_SALUTATION = computed(
    () => this.metadataTranslation()?.CONTACT_SALUTATION,
  );

  public readonly workObjectIcon = computed(() =>
    this.workObjectDOM()?.workObjectTypeId
      ? getWorkObjectIconByWorkObjectTypeId(
          this.workObjectDOM().workObjectTypeId,
        )
      : (WHIconENUM.WorkheldIcon as string),
  );
  public readonly isProject = computed(
    () => this.workObjectDOM()?.workObjectTypeId === 1,
  );
  public readonly isCase = computed(
    () => this.workObjectDOM()?.workObjectTypeId === 2,
  );
  public readonly isStandingOrder = computed(
    () => this.workObjectDOM()?.workObjectTypeId === 3,
  );

  constructor() {
    toObservable(this.detailURL)
      .pipe(
        filter((detailURL: string) => !this.isExternalInput() && !!detailURL),
        switchMap((detailURL: string) =>
          this.dataService.getByUrl(detailURL).pipe(
            catchError((error) => {
              ErrorHandlerService.handleGlobalError(error);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((workObjectDTO: IWHWorkObjectDTO) => {
        const workObjectDOM: WHWorkObjectDOM =
          this.dataModelHelperService.initWorkObjectTypeDOM(workObjectDTO);
        this.workObjectDOM.set(workObjectDOM);
        this.titleLabel.set(
          workObjectUiConfig$.value.find((enumModelDOM: IWHUIConfigModel) => {
            return enumModelDOM.typeId === workObjectDOM.workObjectTypeId;
          }).translateKey,
        );
      });
  }

  public get contacPersonIcon(): string {
    return WHIconENUM.UserIcon as string;
  }

  public async contactCrud(contactDTO?: IWHFlowContactDTO) {
    const result = await firstValueFrom(
      this.appDialogService
        .openContactMatDialog({
          minWidth: '60%',
          data: {
            contactDTO,
          },
        })
        .afterClosed(),
    );

    if (typeof result === 'string') {
      // Returning string means delete
      const payload: IWHWorkObjectDTO = structuredClone(this.workObjectDOM());
      payload['removeContacts'] = [{ id: result }];
      this.updateContacts(payload);
    } else if (result) {
      // Returning object means update or create
      const payload: IWHWorkObjectDTO = structuredClone(this.workObjectDOM());
      payload.contacts = [result];
      this.updateContacts(payload);
    }
  }

  openExecutionGroupDetails() {
    this.router.navigate([
      WHRouteZeroENUM.EXECUTION_GROUPS,
      this.workObjectDOM().executionGroupId,
    ]);
  }

  public updateContacts(workObjectDOM: IWHWorkObjectDTO): void {
    const endpointConfig: IWHWorkObjectEndpointConfigModel = {
      apiUrl: environment.apiUrl + FLOW_GATEWAY,
      workObjectTypeId: this.workObjectDOM().workObjectTypeId,
      workObjectId: this.workObjectDOM().id,
    };
    this.workObjectDataService
      .updateWorkObjectByWorkObjectType(endpointConfig, workObjectDOM)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe((workObjectDTO: IWHWorkObjectDTO) => {
        const workObjectDOM: WHWorkObjectDOM =
          this.dataModelHelperService.initWorkObjectTypeDOM(workObjectDTO);
        this.store.dispatch(
          new SetWHWorkObjectDOM({
            workObjectTypeId: workObjectDOM.workObjectTypeId,
            workObjectDOM: workObjectDOM,
          }),
        );
      });
  }

  public get infoIcon() {
    return WHIconENUM.InfoIcon as string;
  }
}
